import React, { useState, useEffect } from "react";
import {
  FaHome,
  FaClipboardList,
  FaChartBar,
  FaBoxOpen,
  FaUsers,
  FaSignOutAlt,
  FaChevronLeft,
  FaChevronRight,
  FaQuestionCircle,
} from "react-icons/fa";
import "./Sidebar.css";
import { signOut, onAuthStateChanged } from "firebase/auth";
import { auth } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useNavigate } from "react-router-dom";

function Sidebar() {
  const navigate = useNavigate();
  const [role, setRole] = useState(localStorage.getItem("role") || null);
  const [collapsed, setCollapsed] = useState(true);
  const [hovered, setHovered] = useState(null);

  // Sync sidebar width with CSS variable for layout coordination
  useEffect(() => {
    const width = collapsed ? "64px" : "270px";
    document.documentElement.style.setProperty("--sb-width", width);
  }, [collapsed]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      try {
        if (user) {
          const snap = await getDoc(doc(db, "users", user.uid));
          if (snap.exists()) {
            const r = snap.data().role;
            setRole(r);
            localStorage.setItem("role", r);
          }
        }
      } catch (err) {
        console.warn("Sidebar role fetch failed (likely offline):", err);
      }
    });
    return () => unsub();
  }, []);

  const menuItems = [
    // 1. C&F (OUR CLIENT)
    { divider: true, label: "C&F (OUR CLIENT)", hidden: !["superadmin", "admin", "operator"].includes(role) },
    { 
        title: "DASHBOARD", 
        icon: <FaHome />, 
        path: "/cnf-dashboard",
        hidden: !["superadmin", "admin", "operator"].includes(role)
    },
    { 
        title: "BILL DISTRIBUTOR", 
        icon: <FaClipboardList />, 
        path: "/cnf-to-distributor-billing",
        hidden: !["superadmin", "admin", "operator"].includes(role),
        children: [
          { title: "NEW INVOICE", path: "/cnf-to-distributor-billing" },
          { title: "VIEW INVOICES", path: "/distributor-invoices" },
        ]
    },
    {
      title: "PURCHASE",
      icon: <FaBoxOpen />,
      path: "/cnf-purchase",
      hidden: !["superadmin", "admin", "operator"].includes(role),
      children: [
        { title: "NEW PURCHASE", path: "/cnf-purchase" },
        { title: "INWARD LIST", path: "/inward-list" },
      ],
    },
    {
      title: "INVENTORY",
      icon: <FaBoxOpen />,
      path: "/cnf-stock-ledger",
      hidden: !["superadmin", "admin", "operator"].includes(role),
      children: [
        { title: "STOCK LEDGER", path: "/cnf-stock-ledger" },
        { title: "STOCK LEDGER (BAGS)", path: "/cnf-stock-ledger-bags" },
      ],
    },
    {
      title: "MASTER",
      icon: <FaBoxOpen />,
      path: "/sku-master",
      hidden: !["superadmin", "admin", "operator"].includes(role),
      children: [
        { title: "SKU MASTER", path: "/sku-master" },
        { title: "DISTRIBUTION MASTER", path: "/distributor-manager" },
        { title: "SUPPLIER MASTER", path: "/supplier-manager" },
        { title: "C&F MASTER", path: "/cnf-manager" },
      ],
    },

    // 2. DISTRIBUTOR
    { divider: true, label: "DISTRIBUTOR", hidden: !["superadmin", "admin", "distributor", "operator"].includes(role) },
    {
      title: "BILL",
      icon: <FaClipboardList />,
      path: "/distributor-bill-ds",
      hidden: !["superadmin", "admin", "distributor"].includes(role),
      children: [
        { title: "BILL DS", path: "/distributor-bill-ds" },
        { title: "BILL OUTLET", path: "/ds-bill-outlet" },
      ],
    },
    {
      title: "STOCK LEDGER",
      icon: <FaBoxOpen />,
      path: "/distributor-inventory-report",
      hidden: !["superadmin", "admin", "distributor", "operator"].includes(role),
    },
    {
      title: "DS REPORTS",
      icon: <FaChartBar />,
      path: "/ds-order-report",
      hidden: !["superadmin", "admin", "distributor", "operator"].includes(role),
      children: [
        { title: "DS ORDER REPORT", path: "/ds-order-report" },
        { title: "DS TRAVEL LOG", path: "/ds-travel-report" },
      ],
    },
    {
      title: "MASTER",
      icon: <FaBoxOpen />,
      path: "/ds-manager",
      hidden: !["superadmin", "admin", "distributor", "operator", "ds_manager"].includes(role),
      children: [
        { title: "DS MASTER", path: "/ds-manager" },
        { title: "OUTLET MASTER", path: "/outlet-master", hidden: role === "ds_manager" },
      ],
    },
    
    // 3. RSM (REGIONAL STRATEGY)
    { divider: true, label: "REGIONAL STRATEGY", hidden: role !== "rsm" },
    {
      title: "RSM DASHBOARD",
      icon: <FaHome />,
      path: "/rsm-dashboard",
      hidden: role !== "rsm"
    },
    {
      title: "SALES REPORTS",
      icon: <FaChartBar />,
      path: "/rsm/sales-reports",
      hidden: role !== "rsm"
    },
    {
      title: "STOCK REPORTS",
      icon: <FaBoxOpen />,
      path: "/rsm/stock-reports",
      hidden: role !== "rsm"
    },
    {
      title: "MASTER VIEW",
      icon: <FaBoxOpen />,
      path: "/sku-master",
      hidden: role !== "rsm",
      children: [
        { title: "SKU MASTER", path: "/sku-master" },
        { title: "DISTRIBUTOR MASTER", path: "/distributor-manager" },
        { title: "DS MASTER", path: "/ds-manager" },
        { title: "OUTLET MASTER", path: "/outlet-master" },
      ],
    },

    // 4. HELP GUIDE
    { divider: true, label: "HELP GUIDE", hidden: ["rsm", "ds_manager"].includes(role) },
    { title: "HELP GUIDE", icon: <FaQuestionCircle />, path: "/help", hidden: ["rsm", "ds_manager"].includes(role) },
  ];

  const goTo = (p) => navigate(p);

  return (
    <div
      className={`sb-ultra ${collapsed ? "collapsed" : ""}`}
      onMouseEnter={() => setCollapsed(false)}
      onMouseLeave={() => {
        setCollapsed(true);
        setHovered(null);
      }}
    >
      <div className="sb-brand">
        <div className="sb-logo">
          <img src="/logo.svg" alt="logo" style={{ width: 44, height: 44, borderRadius: 8, objectFit: "contain" }} />
        </div>
        {!collapsed && <span className="sb-brand-text">QuickBill</span>}
      </div>

      <ul className="sb-menu">
        {menuItems.map((item, idx) => {
          if (item.hidden) return null;

          if (item.divider) {
            return (
              <div key={idx} className="sb-divider-label">
                {item.label}
              </div>
            );
          }

          const active = window.location.pathname === item.path;
          const hasChildren = Array.isArray(item.children);

          return (
            <li
              key={idx}
              className={active ? "active" : ""}
              onClick={() => !hasChildren && goTo(item.path)}
              onMouseEnter={() => hasChildren && setHovered(item.title)}
              onMouseLeave={() => hasChildren && setHovered(null)}
              title={item.title}
            >
              <span className="sb-icon">{item.icon}</span>
              <span className="sb-label">{item.title}</span>

              {active && <div className="sb-active-pill" />}

              {hasChildren && hovered === item.title && (
                <div className="sb-flyout">
                  {item.children.map((child, cidx) => (
                    <div
                      key={cidx}
                      className="sb-flyout-item"
                      onClick={() => goTo(child.path)}
                    >
                      {child.title}
                    </div>
                  ))}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      <div className="sb-bottom">
        <div
          className="sb-logout"
          onClick={() =>
            signOut(auth).then(() => {
              navigate("/");
            })
          }
        >
          <FaSignOutAlt className="sb-icon" />
          <span className="sb-label">Logout</span>
        </div>

        <button
          className="sb-collapse-toggle"
          onClick={() => setCollapsed((prev) => !prev)}
        >
          {collapsed ? <FaChevronRight /> : <FaChevronLeft />}
        </button>
      </div>
    </div>
  );
}

export default Sidebar;
